import React, { useState } from 'react';
import { Chip } from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { useRedemptions } from '../hooks/useCampaigns';
import { formatDateTime, formatVnd } from '../format';
import type { Redemption, RedemptionStatus } from '../types';

const COLORS: Record<RedemptionStatus, 'warning' | 'success' | 'default'> = {
  RESERVED: 'warning',
  REDEEMED: 'success',
  RELEASED: 'default',
};

export const RedemptionsTab: React.FC<{ campaignId: string }> = ({ campaignId }) => {
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const { data, isLoading } = useRedemptions(campaignId, pagination.page + 1, pagination.pageSize);

  const columns: GridColDef<Redemption>[] = [
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      minWidth: 220,
      valueGetter: (_v, row) => row.user.email,
    },
    { field: 'coupon', headerName: 'Code', width: 200, valueGetter: (_v, row) => row.coupon.code },
    {
      field: 'listAmountMinor',
      headerName: 'List',
      width: 120,
      valueGetter: (_v, row) => formatVnd(row.listAmountMinor),
    },
    {
      field: 'discountMinor',
      headerName: 'Discount',
      width: 120,
      valueGetter: (_v, row) => formatVnd(row.discountMinor),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.row.status}
          color={COLORS[p.row.status]}
          variant={p.row.status === 'RELEASED' ? 'outlined' : 'filled'}
        />
      ),
    },
    {
      field: 'releaseReason',
      headerName: 'Reason',
      width: 120,
      valueGetter: (_v, row) => row.releaseReason ?? '',
    },
    {
      field: 'reservedAt',
      headerName: 'Reserved',
      width: 180,
      valueGetter: (_v, row) => formatDateTime(row.reservedAt),
    },
    {
      field: 'redeemedAt',
      headerName: 'Redeemed',
      width: 180,
      valueGetter: (_v, row) => formatDateTime(row.redeemedAt),
    },
  ];

  return (
    <DataGrid
      autoHeight
      rows={data?.items ?? []}
      getRowId={(row) => row.publicId}
      columns={columns}
      loading={isLoading}
      rowCount={data?.total ?? 0}
      paginationMode="server"
      paginationModel={pagination}
      onPaginationModelChange={setPagination}
      pageSizeOptions={[25, 50, 100]}
      disableRowSelectionOnClick
      localeText={{ noRowsLabel: 'No redemptions yet.' }}
    />
  );
};
